import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./interested_clients.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import maleDefaultPic from "../../assets/male-profile-picture.png";
import femaleDefaultPic from "../../assets/female-profile-picture.png";
import closeIcon from "../../assets/close_icon.png";
import pureVegIcon from "../../assets/pure-veg-symbol.jpg";
import nonVegIcon from "../../assets/non-veg-symbol.jpg";
import * as XLSX from "xlsx";
import saveAs from "file-saver";

interface User {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  conditions: object;
  startingBmi: string;
  languageCode: string;
  profilePicture: string;
  activePlans: any;
  historyPlans: any;
  weightEntries: object;
  dietPlan: object;
  createdAt: Date;
  updatedAt: Date;
  totalCalorieSearchApiCalls: number;
  activeCalorieTrackerBySearchPlan: any;
  historyCalorieTrackerBySearchPlans: any;
  dietaryType: string;
  lastOnline: Date;
  registrationDevice: string;
  goalWeightLoss: string;
  goalWeightGain: string;
  goalMuscleGain: string;
  goalHealthIssues: string;
  appliedCoupon: string;
  assignedTo: string | null;
  clientStatus: string;
}

const InterestedClients = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [expandedUsers, setExpandedUsers] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [showSalesPersonModal, setShowSalesPersonModal] =
    useState<boolean>(false);
  const [selectedUserPhone, setSelectedUserPhone] = useState<string>("");
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState<boolean>(false);

  const salesPersons = [
    "Sumera",
    "Khushboo",
    "Lata",
    "Manoj",
    "Chandni",
    "Amit",
  ];

  // Fetch user details and users
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      setUsers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(users);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    // Convert to a binary Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "Employee_Details.xlsx");
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Expand or collapse individual user cards
  const toggleExpanded = (userId: string) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const timeAgo = (dateString: string): string => {
    const now = new Date().getTime(); // Convert to timestamp
    const pastDate = new Date(dateString).getTime(); // Convert to timestamp
    const diffInSeconds = Math.floor((now - pastDate) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)} min ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)} h ago`;
    } else if (diffInSeconds < 2592000) {
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    } else if (diffInSeconds < 31536000) {
      return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    } else {
      return `${Math.floor(diffInSeconds / 31536000)} years ago`;
    }
  };

  const openAssignSalesPersonModal = (phoneNumber: string) => {
    setSelectedUserPhone(phoneNumber);
    setSelectedSalesPerson("");
    setShowSalesPersonModal(true);
  };

  const assignSalesPerson = async () => {
    if (!selectedSalesPerson) {
      alert("Please select a sales person");
      return;
    }

    setIsAssigning(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          assignedTo: selectedSalesPerson,
        },
        config
      );

      // Update the local state to reflect the change
      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, assignedTo: selectedSalesPerson }
            : user
        )
      );

      setShowSalesPersonModal(false);
    } catch (error) {
      console.error("Failed to assign sales person:", error);
      alert("Failed to assign sales person. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  };

  function convertHeightToMeters(heightString: string): number {
    if (!heightString) return 0;

    if (heightString.includes("ft")) {
      const feetPattern = /(\d+)\s*ft/;
      const inchesPattern = /(\d+)\s*in/;

      const feetMatch = heightString.match(feetPattern);
      const inchesMatch = heightString.match(inchesPattern);

      const feet = feetMatch ? parseFloat(feetMatch[1]) : 0.0;
      const inches = inchesMatch ? parseFloat(inchesMatch[1]) : 0.0;

      const totalInches = feet * 12 + inches;
      return totalInches * 0.0254; // Convert inches to meters
    } else if (heightString.includes("cm")) {
      const valuePart = heightString.replace(/[^\d.]/g, "");
      const heightInCm = parseFloat(valuePart) || 0.0;
      return heightInCm / 100; // Convert cm to meters
    }
    return 0;
  }

  const calculateMinNormalWeightRange = (
    bmi: number,
    heightInMeter: number
  ) => {
    const result = bmi * heightInMeter * heightInMeter;
    return result;
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  // Filter users based on phone number
  const filteredUsers = users.filter((user) =>
    user.phoneNumber.includes(searchTerm)
  );

  return (
    <div className="search-user-by-phone-container">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Sales Person Assignment Modal */}
          {showSalesPersonModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Assign Sales Person</h3>
                <p>Select a sales person for this user:</p>
                <select
                  value={selectedSalesPerson}
                  onChange={(e) => setSelectedSalesPerson(e.target.value)}
                  className="sales-person-select"
                >
                  <option value="">Select Sales Person</option>
                  {salesPersons.map((person) => (
                    <option key={person} value={person}>
                      {person}
                    </option>
                  ))}
                </select>
                <div className="modal-buttons">
                  <button
                    onClick={() => setShowSalesPersonModal(false)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={assignSalesPerson}
                    className="assign-button"
                    disabled={isAssigning || !selectedSalesPerson}
                  >
                    {isAssigning ? "Assigning..." : "Assign"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search Box */}
          <div
            className="search-box"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <h2>Report Tracker</h2>
            <input
              type="text"
              placeholder="Search by Phone Number"
              style={{ marginLeft: "auto", marginRight: "auto" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span
              style={{
                background: "black",
                color: "white",
                padding: "5px 10px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "20px",
                marginLeft: "auto",
                marginRight: "20px",
              }}
              onClick={() => {
                exportToExcel();
              }}
            >
              Generate Report (.xlsx file)
            </span>
          </div>

          <div>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user._id}
                  className="user-list"
                  style={{ flexDirection: "column", width: "98%" }}
                >
                  {user?.clientStatus == "Interested/Follow up" ? (
                    <div className="user-card">
                      <div className="user-details">
                        <p>
                          <strong>Name:</strong> <span>{user.name}</span>
                        </p>
                        <p>
                          <strong>Phone Number:</strong>{" "}
                          <span>{user.phoneNumber}</span>
                        </p>
                        <p>
                          <strong>Email:</strong> <span>{user.email}</span>
                        </p>
                        <p>
                          <strong>Gender:</strong> <span>{user.gender}</span>
                        </p>
                        <p>
                          <strong>Age:</strong> <span>{user.age}</span>
                        </p>
                        <p>
                          <strong>Height:</strong> <span>{user.height}</span>
                        </p>
                        <p>
                          <strong>Weight:</strong> <span>{user.weight}</span>
                        </p>
                        <p>
                          <strong>BMI at the time of joining:</strong>{" "}
                          <span>{parseFloat(user.startingBmi).toFixed(1)}</span>
                        </p>
                        <p>
                          <strong>Normal Weight Should be:</strong>{" "}
                          <span>
                            {calculateMinNormalWeightRange(
                              18.5,
                              convertHeightToMeters(user.height)
                            ).toFixed(1)}{" "}
                            kg
                          </span>{" "}
                          -{" "}
                          <span>
                            {calculateMinNormalWeightRange(
                              24.9,
                              convertHeightToMeters(user.height)
                            ).toFixed(1)}{" "}
                            kg
                          </span>
                        </p>
                        <p style={{ display: "Flex", gap: "10px" }}>
                          <strong>Dietary Type:</strong>
                          {user.dietaryType == "Vegetarian" ? (
                            <div className="veg-container">
                              <img src={pureVegIcon} height="20px"></img>{" "}
                              <span>Vegetarian</span>
                            </div>
                          ) : (
                            <div className="nonveg-container">
                              <img src={nonVegIcon} height="20px"></img>
                              <span>Non-Vegitarian</span>
                            </div>
                          )}
                        </p>
                        <span
                          style={{
                            display: "flex",
                            gap: "10px",
                            fontSize: "20px",
                            fontWeight: "600",
                          }}
                        >
                          <strong>Goal:</strong>{" "}
                          <span style={{ display: "flex", gap: "10px" }}>
                            {user.goalWeightLoss == "true" ? (
                              <span
                                style={{
                                  color: "Green",
                                  background: "white",
                                  padding: "0px 10px",
                                  borderRadius: "4px",
                                }}
                              >
                                "Weight Loss"
                              </span>
                            ) : (
                              ""
                            )}
                            {user.goalWeightGain == "true" ? (
                              <span
                                style={{
                                  color: "Green",
                                  background: "white",
                                  padding: "0px 10px",
                                  borderRadius: "4px",
                                }}
                              >
                                "Weight Gain"
                              </span>
                            ) : (
                              ""
                            )}
                            {user.goalMuscleGain == "true" ? (
                              <span
                                style={{
                                  color: "Green",
                                  background: "white",
                                  padding: "0px 10px",
                                  borderRadius: "4px",
                                }}
                              >
                                "Muscle Gain"
                              </span>
                            ) : (
                              ""
                            )}
                            {user.goalHealthIssues == "true" ? (
                              <span
                                style={{
                                  color: "Green",
                                  background: "white",
                                  padding: "0px 10px",
                                  borderRadius: "4px",
                                }}
                              >
                                "Other Health issues"
                              </span>
                            ) : (
                              ""
                            )}
                          </span>
                        </span>{" "}
                        {/* Show more details only if the card is expanded */}
                        {expandedUsers[user._id] && (
                          <>
                            <p>
                              <strong>Activity:</strong>{" "}
                              <span>{user.activity}</span>
                            </p>
                            {user.medicalConcern == "Yes" ? (
                              <span
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "600",
                                  display: "flex",
                                  gap: "10px",
                                }}
                              >
                                <strong>Diseases: </strong>
                                <div
                                  style={{
                                    color: "red",
                                    background: "white",
                                    padding: "0px 10px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {Object.entries(user.conditions)
                                    .filter(([key, value]) => value === true)
                                    .map(([key]) => {
                                      const diseaseNames = {
                                        diabetes: "Diabetes",
                                        menopause: "Menopause",
                                        pcosPcod: "PCOS/PCOD",
                                        cardiovascular: "Cardiovascular",
                                        immunity: "Immunity",
                                        hairAndSkin: "Hair & Skin",
                                        highUricAcid: "High Uric Acid",
                                        anemia: "Anemia",
                                        ulcerativeColitis: "Ulcerative Colitis",
                                        crohnsDisease: "Crohn's Disease",
                                        sle: "SLE",
                                        psoriasis: "Psoriasis",
                                        hashimotos: "Hashimoto's",
                                        irritableBowelSyndrome:
                                          "Irritable Bowel Syndrome",
                                        thyroidism: "Thyroidism",
                                        antiCancerDiets: "Anti-Cancer Diets",
                                        otherMedicalConditions:
                                          user.conditions[
                                            "otherMedicalConditionsDes"
                                          ],
                                      };

                                      return diseaseNames[key] || null;
                                    })
                                    .filter(Boolean)
                                    .join(" | ")}
                                </div>
                              </span>
                            ) : (
                              <span
                                style={{ fontSize: "20px", fontWeight: "600" }}
                              >
                                <strong>Diseases:</strong>{" "}
                                <span style={{ color: "greenyellow" }}>
                                  No disease
                                </span>
                              </span>
                            )}
                            <p
                              style={{
                                display: "flex",
                                flexDirection: user.activePlans[0]?.planName
                                  ? "column"
                                  : "row",
                                alignItems: "baseline",
                                gap: "5px",
                              }}
                            >
                              <strong>Active Plan:</strong>{" "}
                              {user.activePlans[0]?.planName ? (
                                <div className="history-plans-card">
                                  {user.activePlans.map(
                                    (plan: any, index: number) => (
                                      <span
                                        className="history-plans-card-item"
                                        key={index}
                                      >
                                        <span style={{ fontWeight: "500" }}>
                                          {index +
                                            1 +
                                            ". " +
                                            plan.planName +
                                            ":"}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Price: ₹" + plan.price}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Start date: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.startDate))}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • End date: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.endDate))}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Coupon Code used (If any): " +
                                            (user.appliedCoupon != null
                                              ? user.appliedCoupon
                                              : "N/A")}
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "red" }}>
                                  No Active plan
                                </span>
                              )}
                            </p>
                            <p
                              style={{
                                display: "flex",
                                flexDirection: user.historyPlans[0]?.planName
                                  ? "column"
                                  : "row",
                                alignItems: "baseline",
                                gap: "5px",
                              }}
                            >
                              <strong>History Plans:</strong>
                              {user.historyPlans[0]?.planName ? (
                                <div className="history-plans-card">
                                  {user.historyPlans.map(
                                    (plan: any, index: number) => (
                                      <span
                                        className="history-plans-card-item"
                                        key={index}
                                      >
                                        <span style={{ fontWeight: "500" }}>
                                          {index +
                                            1 +
                                            ". " +
                                            plan.planName +
                                            ":"}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Price: ₹" + plan.price}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Start date: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.startDate))}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Expired on: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.endDate))}
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "red" }}>
                                  No History plan
                                </span>
                              )}
                            </p>
                            <p
                              style={{
                                display: "flex",
                                flexDirection: user
                                  .activeCalorieTrackerBySearchPlan[0]?.price
                                  ? "column"
                                  : "row",
                                alignItems: "baseline",
                                gap: "5px",
                              }}
                            >
                              <strong>Calorie Tracker Active Plan:</strong>
                              {user.activeCalorieTrackerBySearchPlan[0]
                                ?.price ? (
                                <div className="history-plans-card">
                                  {user.activeCalorieTrackerBySearchPlan.map(
                                    (plan: any, index: number) => (
                                      <span
                                        className="history-plans-card-item"
                                        key={index}
                                      >
                                        <span style={{ fontWeight: "500" }}>
                                          {index +
                                            1 +
                                            ". " +
                                            "One Month Plan" +
                                            ":"}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Price: ₹" + plan.price}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Start date: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.startDate))}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • End date: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.endDate))}
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "red" }}>
                                  No Active plan
                                </span>
                              )}
                            </p>
                            <p
                              style={{
                                display: "flex",
                                flexDirection: user.historyPlans[0]?.planName
                                  ? "column"
                                  : "row",
                                alignItems: "baseline",
                                gap: "5px",
                              }}
                            >
                              <strong>Calorie Tracker History Plans:</strong>
                              {user.historyPlans[0]
                                ?.historyCalorieTrackerBySearchPlans ? (
                                <div className="history-plans-card">
                                  {user.historyPlans.map(
                                    (plan: any, index: number) => (
                                      <span
                                        className="history-plans-card-item"
                                        key={index}
                                      >
                                        <span style={{ fontWeight: "500" }}>
                                          {index +
                                            1 +
                                            ". " +
                                            plan.historyCalorieTrackerBySearchPlans +
                                            ":"}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Price: ₹" + plan.price}{" "}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Start date: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.startDate))}
                                        </span>
                                        <span style={{ paddingLeft: "10px" }}>
                                          {"  • Expired on: " +
                                            new Intl.DateTimeFormat("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              timeZone: "Asia/Kolkata",
                                            }).format(new Date(plan.endDate))}
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: "red" }}>
                                  No History plan
                                </span>
                              )}
                            </p>
                            <p>
                              <strong>Food Calorie Search Count:</strong>{" "}
                              <span>{user.totalCalorieSearchApiCalls}</span>
                            </p>
                            <p>
                              <strong>Registered on:</strong>{" "}
                              <span>
                                {new Date(user.createdAt)
                                  .toLocaleString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                  })
                                  .replace(",", "")
                                  .replace(/(\d)(?=\d{2}\:)/, "$1 ")}
                              </span>
                            </p>
                            <p>
                              <strong>
                                Updated {user.gender == "Male" ? "his" : "her"}{" "}
                                Profile on:
                              </strong>{" "}
                              <span>
                                {new Date(user.updatedAt)
                                  .toLocaleString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                  })
                                  .replace(",", "")
                                  .replace(/(\d)(?=\d{2}\:)/, "$1 ")}
                              </span>
                            </p>
                          </>
                        )}
                        <button
                          className="toggle-details-button"
                          onClick={() => toggleExpanded(user._id)}
                        >
                          {expandedUsers[user._id]
                            ? "View Less Details"
                            : "View More Details"}
                        </button>
                      </div>
                      <span className="user-card-profile">
                        <span
                          style={{
                            background: "yellow",
                            color: "black",
                            fontWeight: "700",
                            fontSize: "18px",
                            width: "fit-content",
                            padding: "0px 20px",
                            borderRadius: "6px",
                            marginLeft: "auto",
                            marginRight: "auto",
                            marginBottom: "10px",
                          }}
                        >
                          Last seen: {timeAgo(user?.lastOnline.toString())}
                        </span>
                        <span
                          style={{
                            background: "yellow",
                            color: "black",
                            fontWeight: "700",
                            fontSize: "18px",
                            width: "170px",
                            padding: "0px 20px",
                            borderRadius: "6px",
                            marginLeft: "auto",
                            marginRight: "auto",
                            marginBottom: "10px",
                          }}
                        >
                          {user?.registrationDevice}
                        </span>
                        {user?.profilePicture != null ? (
                          <img
                            className="user-card-profile-pic"
                            src={user?.profilePicture}
                          ></img>
                        ) : user.gender == "Male" ? (
                          <img
                            className="user-card-profile-pic"
                            src={maleDefaultPic}
                          ></img>
                        ) : (
                          <img
                            className="user-card-profile-pic"
                            src={femaleDefaultPic}
                          ></img>
                        )}
                        {user.activePlans[0]?.planName != null ? (
                          <span className="user-card-profile-tag">
                            Subscribed
                          </span>
                        ) : (
                          <span className="user-card-profile-tag-not">
                            Not Subscribed
                          </span>
                        )}
                        <p>
                          <strong>Assigned To:</strong>{" "}
                          <span
                            style={{
                              color: user.assignedTo ? "greenyellow" : "red",
                              fontWeight: "bold",
                              fontSize: "18px",
                            }}
                          >
                            {user.assignedTo || "Not Assigned"}
                          </span>
                        </p>
                        <button
                          className="assign-sales-person-button"
                          onClick={() =>
                            openAssignSalesPersonModal(user.phoneNumber)
                          }
                        >
                          Assign Sales Person
                        </button>
                      </span>
                    </div>
                  ) : (
                    <span style={{ display: "none" }}></span>
                  )}
                </div>
              ))
            ) : (
              <p>No User found.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InterestedClients;
